/**
 * PharmacyFilters.tsx — the dispensing queue's advanced filter panel.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: pharmacy dispensing lane.
 *
 * WHY THIS EXISTS (2026-09-08). Two staff-marked test orders sat in Pharmacy
 * Xpress's live queue for six days. The first fix removed them from the pharmacy
 * entirely, which closed the hole and opened a smaller one: the pharmacy had
 * already been emailed about both orders, and an order that silently vanishes
 * after an alert arrives is the thing that generates a phone call. The ruling is
 * that they stay — off the working queue by default, findable on purpose, marked
 * as tests, and impossible to dispense.
 *
 * THE FOURTH SCREEN, AND STILL NOT A FOURTH IMPLEMENTATION. The CRM pipeline,
 * the Patients directory and the Payments board all ask a version of this
 * question behind a "Filters (n)" button with a modal draft and a removable
 * chip. Every control here comes from `ui/filters.tsx` and the frame is the
 * estate's `DialogShell`, so a dispensary learns a grammar that already exists
 * rather than a fourth one.
 *
 * ⚠️ BUT THE WORDS ARE DIFFERENT, DELIBERATELY. The three staff screens say
 * "Show test records" — one idea under one name, and PaymentsFilters copies it
 * verbatim for exactly that reason. This screen says "SHOW TEST ORDERS",
 * because the CTO specified those words for the pharmacy and because the
 * audience is different: a pharmacist works to a queue of ORDERS, has no idea
 * what a "record" is in our CRM, and is an external company reading an unfamiliar
 * console. Consistency of vocabulary is worth a lot inside one staff console and
 * nothing at all across an organisational boundary.
 *
 * THE DRAFT IS THE POINT, as on the other three: applying on every keystroke
 * would fire a server read per click. Apply commits once.
 */
import { useState } from 'react';
import { DialogShell } from '../ui/ConfirmDialog';
import { FilterFooter, FilterSection, FilterTick, filterNoteStyle } from '../ui/filters';

export interface PharmacyFilterState {
  /** The opt-in view — sends orders' `includeTest=true`. */
  showTest: boolean;
}

export const EMPTY_PHARMACY_FILTERS: PharmacyFilterState = { showTest: false };

/** How many distinct choices a state represents — the bar's badge count. */
export function activePharmacyFilterCount(f: PharmacyFilterState): number {
  return f.showTest ? 1 : 0;
}

/** The chip wording for an applied `showTest`, shared with the toolbar. */
export const PHARMACY_TEST_CHIP = 'Test orders shown';

/** The label, exported so the queue and its spec name the control the same way. */
export const PHARMACY_TEST_FILTER_LABEL = 'Show test orders';

export function PharmacyFilters({
  applied,
  onApply,
  onClose,
}: {
  applied: PharmacyFilterState;
  onApply: (next: PharmacyFilterState) => void;
  onClose: () => void;
}) {
  // Seeded from what is applied, so opening shows the current state and closing
  // without applying changes nothing.
  const [draft, setDraft] = useState<PharmacyFilterState>(applied);

  return (
    <DialogShell title="Advanced filters" onCancel={onClose} width="min(560px, 100%)">
      <FilterSection label="Test orders" hint="Rehearsals run by the clinic. Not real patients.">
        <FilterTick
          label={PHARMACY_TEST_FILTER_LABEL}
          describedBy="pharmacy-filters-test-hint"
          checked={draft.showTest}
          onChange={() => setDraft((d) => ({ ...d, showTest: !d.showTest }))}
        />
        {/* ⚠️ THE HINT SAYS WHAT THE PHARMACIST ACTUALLY NEEDS TO KNOW, which is
            not "this adds rows". They may have arrived here from a disregard
            email, so the hint answers the question that brought them: yes, the
            order you were told about is here, and no, you still cannot dispense
            it. Saying the refusal out loud in the panel means nobody switches
            the filter on, finds the order and discovers the block by clicking a
            button that then fails. */}
        <p id="pharmacy-filters-test-hint" style={{ ...filterNoteStyle, margin: '4px 0 0 30px' }}>
          The clinic marks an order as a test when it is a rehearsal rather than a real patient. These are
          kept out of your queue so they are not mistaken for work. Switching this on shows them, with the
          clinic&rsquo;s reason &mdash; they still cannot be dispensed.
        </p>
      </FilterSection>

      <FilterFooter onClear={() => setDraft(EMPTY_PHARMACY_FILTERS)} onApply={() => onApply(draft)} />
    </DialogShell>
  );
}
